import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals';
import { useFrontChatControl } from '@features/front-chat';
import { PracticeSettingsCatalogLayout } from '@features/organizations/components/PracticeSettings/PracticeSettingsCatalogLayout';
import { CleanupWizardEntry } from '@features/practice-cleanup';
import { useCan, useOrganizationId } from '@features/user';
import TuneIcon from '@mui/icons-material/Tune';
import { Alert, Box, Button, Stack } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';
import { useModal } from '@store/hooks';

import { AppointmentTypeCatalogContainer } from './AppointmentTypeCatalogContainer';

/**
 * Full-height route for the evergreen catalog (GDN-2335):
 * /practice/appointment-types/catalog. A data-dense workbench wants a
 * bounded viewport. PracticeLayout supplies its remaining height in embedded
 * mode, keeping the sidebar and catalog footer visible. Standalone hosts retain
 * the back link to the overview.
 *
 * Header actions live here (not in the catalog's filter bar): Manage opens
 * the org management modal; the cleanup wizard launches via its own entry
 * (button variant) — the catalog is where admins SEE the duplicates, so
 * it's where the wizard should be offered.
 */
export const AppointmentTypeCatalogPage: React.FC<{ embedded?: boolean }> = ({
  embedded = false,
}) => {
  const navigate = useNavigate();
  const isEnabled = useFeatureGate(FeatureGates.PRACTICE_TYPE_CATALOG);
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const organizationId = useOrganizationId() ?? undefined;
  const { typeCatalog } = useEventTracking();
  const { open: openModal } = useModal();
  const { hideLauncher, showLauncher } = useFrontChatControl();

  // The Front chat FAB sits bottom-left — exactly over the footer's
  // selection summary. Hide it for the workbench, restore on leave.
  useEffect(() => {
    hideLauncher();
    return (): void => {
      showLauncher();
    };
  }, [hideLauncher, showLauncher]);

  const handleBack = useCallback((): void => {
    navigate('/practice/appointment-types');
  }, [navigate]);

  const handleManageTypes = useCallback((): void => {
    typeCatalog.manageTypesClicked({ organization_id: organizationId });
    openModal(MODAL_TYPES.APPOINTMENT_TYPES_MANAGEMENT, {
      source: 'practice_type_catalog',
      maxWidth: 'md',
      fullScreenOnMobile: true,
    });
  }, [organizationId, typeCatalog, openModal]);

  // No auto-redirect on a false gate: Statsig may still be initializing on a
  // hard reload, and bouncing a legitimate user is worse than a static
  // fallback with a way back.
  if (!isEnabled || !canManageAppointmentTypes) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleBack}>
              Back
            </Button>
          }
        >
          {canManageAppointmentTypes
            ? 'The appointment-type catalog is not enabled for your practice.'
            : 'You do not have permission to manage practice appointment types.'}
        </Alert>
      </Box>
    );
  }

  return (
    <PracticeSettingsCatalogLayout
      id="appointment-type-catalog-workspace"
      embedded={embedded}
      title="Appointment type catalog"
      subtitle="Review appointment types, their usage, and duplicates across your practice."
      backLabel="Appointment types"
      onBack={handleBack}
      actions={
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap', minWidth: 0 }}>
          <CleanupWizardEntry variant="button" />
          <Button variant="contained" startIcon={<TuneIcon />} onClick={handleManageTypes}>
            Manage appointment types
          </Button>
        </Stack>
      }
    >
      <AppointmentTypeCatalogContainer />
    </PracticeSettingsCatalogLayout>
  );
};

export default AppointmentTypeCatalogPage;
